import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useLazyCommand, useEvent } from '@soap/modules';
import { ArrowLeft, PieChart, GraduationCap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { CurriculumHeader } from '../../components/CurriculumHeader';
import { Button } from '../../ui/button';
import { StudentSelector } from '../../components/StudentSelector';
import { useSelectedStudent } from '../../hooks/useSelectedStudent';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { libraryYearDisplayName } from '../../components/LessonsPage/utils/curriculumUtils';
import { LYW_BRAND } from '../../constants/brandPalette';

// Backend returns only subject-level counts (no topic-level data)
type SubjectRow = {
  e291_SubjectName?: string;
  e291_LessonsInLibrary?: number | null;
  e291_CustomTopicsInCurriculum?: number | null;
  e291_LessonsInStudentCurriculum?: number | null;
  e291_LessonsCompleted?: number | null;
};

type CurriculumYearData = {
  e179_Number?: number | null;
  e179_Name?: string | null;
  e179_YearId?: string | null;
};

type CurriculumKeyStage = {
  e179_Years?: Array<CurriculumYearData>;
};

type CurriculumSubject = {
  e179_KeyStages?: Array<CurriculumKeyStage>;
};

type CurriculumStructureEvent = {
  e179_Subjects?: Array<CurriculumSubject>;
};

type CurriculumYearOption = {
  yearId: string;
  label: string;
};

const VIEW = { idle: 0, loading: 1, ready: 2, noStudent: 3, noData: 4 };

const CHART_KEYS = {
  builtIn: 'builtIn',
  custom: 'custom',
  inPlan: 'inPlan',
  completed: 'completed',
} as const;

const STACK_ORDER = [
  CHART_KEYS.builtIn,
  CHART_KEYS.custom,
  CHART_KEYS.inPlan,
  CHART_KEYS.completed,
] as const;

const R = 4;

/** Recharts horizontal Bar `radius`: [topLeft, topRight, bottomRight, bottomLeft]. */
function stackSegmentRadius(
  segment: (typeof STACK_ORDER)[number],
  values: Record<(typeof STACK_ORDER)[number], number>
): [number, number, number, number] {
  const present = STACK_ORDER.filter((k) => values[k] > 0);
  if (present.length === 0) return [0, 0, 0, 0];
  const first = present[0];
  const last = present[present.length - 1];
  if (first === last) return [R, R, R, R];
  if (segment === first) return [R, 0, 0, R];
  if (segment === last) return [0, R, R, 0];
  return [0, 0, 0, 0];
}

const LABEL_BUILTIN = 'Builtin';
const LABEL_CUSTOM = 'Custom';
const LABEL_COMPLETED = 'Completed';

function parseYearN(label: string): number | null {
  const m = /^Year\s+(\d+)$/i.exec(label.trim());
  return m ? parseInt(m[1], 10) : null;
}

export function AnnualProgressReportPage() {
  const {
    selectedStudentId,
    students,
    setSelectedStudent,
    isLoading: isStudentLoading,
    selectedStudentFirstName
  } = useSelectedStudent();
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [reportData, setReportData] = useState<SubjectRow[] | null>(null);
  const [viewState, setViewState] = useState<number>(VIEW.idle);
  const [curriculumYears, setCurriculumYears] = useState<CurriculumYearOption[]>([]);

  const inStudentPlanLabel =
    selectedStudentFirstName.trim() !== ''
      ? `In ${selectedStudentFirstName.trim()}'s Plan`
      : 'In Plan';

  const [sendGetReport] = useLazyCommand('C291v1_GetAnnualProgressReportData');
  const [sendGetCurriculum] = useLazyCommand('C258v1_GetCurriculumStructure');

  useEvent({
    eventName: 'E291v1_GotAnnualProgressReportData',
    onEventReceived: (event: {
      e291_Subjects?: SubjectRow[];
      e291_LatestEducationSystemYearIdWithData?: string | null;
    }) => {
      const subjects = event?.e291_Subjects ?? [];
      setReportData(subjects);

      if (!selectedYear && event?.e291_LatestEducationSystemYearIdWithData && curriculumYears.length > 0) {
        const latest = String(event.e291_LatestEducationSystemYearIdWithData).trim().toLowerCase();
        const match = curriculumYears.find((y) => y.yearId.toLowerCase() === latest);
        if (match) {
          setSelectedYear(match.yearId);
          return;
        }
      }

      if (
        subjects.length === 0 &&
        event?.e291_LatestEducationSystemYearIdWithData &&
        curriculumYears.length > 0 &&
        selectedYear
      ) {
        const latest = String(event.e291_LatestEducationSystemYearIdWithData).trim().toLowerCase();
        const match = curriculumYears.find((y) => y.yearId.toLowerCase() === latest);
        if (match && selectedYear.toLowerCase() !== match.yearId.toLowerCase()) {
          setSelectedYear(match.yearId);
          return;
        }
      }

      setViewState(VIEW.ready);
    },
  });

  useEvent({
    eventName: 'E179v1_GotCurriculumStructure',
    onEventReceived: (event: CurriculumStructureEvent) => {
      if (event?.e179_Subjects) {
        const yearsSet = new Map<string, CurriculumYearOption>();

        event.e179_Subjects.forEach((subject) => {
          subject.e179_KeyStages?.forEach((keyStage) => {
            keyStage.e179_Years?.forEach((year) => {
              const yearId = year.e179_YearId != null ? String(year.e179_YearId).trim() : '';
              if (!yearId) return;
              const label =
                (year.e179_Name || '').trim() || libraryYearDisplayName(year);
              if (!label) return;
              if (!yearsSet.has(yearId)) {
                yearsSet.set(yearId, { yearId, label });
              }
            });
          });
        });

        const years = Array.from(yearsSet.values()).sort((a, b) => {
          const na = parseYearN(a.label);
          const nb = parseYearN(b.label);
          if (na != null && nb != null) return na - nb;
          if (na != null) return -1;
          if (nb != null) return 1;
          return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
        });

        setCurriculumYears(years);
      }
    },
  });

  useEffect(() => {
    sendGetCurriculum({ C258_CurriculumProviderKey: 'oak' });
  }, []);

  useEffect(() => {
    if (isStudentLoading) {
      setViewState(VIEW.loading);
      return;
    }
    if (!selectedStudentId) {
      setReportData(null);
      setViewState(VIEW.noStudent);
      return;
    }
    if (!selectedYear) {
      if (curriculumYears.length > 0) {
        const highestYear = curriculumYears.reduce((latest, current) => {
          const ln = parseYearN(latest.label);
          const cn = parseYearN(current.label);
          if (ln != null && cn != null) return cn > ln ? current : latest;
          if (cn != null) return current;
          if (ln != null) return latest;
          return current.label.localeCompare(latest.label, undefined, { sensitivity: 'base' }) > 0 ? current : latest;
        });
        setViewState(VIEW.loading);
        sendGetReport({
          C291_StudentId: selectedStudentId,
          C291_EducationSystemYearId: highestYear.yearId,
          C291_CurriculumProviderKey: 'oak',
        });
      }
      return;
    }
    setViewState(VIEW.loading);

    sendGetReport({
      C291_StudentId: selectedStudentId,
      C291_EducationSystemYearId: selectedYear,
      C291_CurriculumProviderKey: 'oak',
    });
  }, [selectedStudentId, selectedYear, isStudentLoading, curriculumYears, sendGetReport]);

  return (
    <div className="p-4 sm:p-6">
        <CurriculumHeader showTabs={false} title="Annual Progress" icon={PieChart} showStudentDropdown={false} />
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <Link to="/app/reports">
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft size={16} />
              Back to Reports
            </Button>
          </Link>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="report-student" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Student
              </Label>
              <StudentSelector
                students={students}
                selectedStudentId={selectedStudentId}
                onSelect={setSelectedStudent}
                showLabel={false}
                placeholder="Select a student"
                className="min-w-[180px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="report-year" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Year
              </Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger id="report-year" className="w-[180px]">
                  <SelectValue
                    placeholder="Select year"
                    label={
                      curriculumYears.find(
                        (y) => y.yearId.toLowerCase() === selectedYear.toLowerCase()
                      )?.label
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {curriculumYears.map((year) => (
                    <SelectItem key={year.yearId} value={year.yearId}>
                      {year.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {viewState === VIEW.noStudent && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <GraduationCap className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a student</h3>
            <p className="text-gray-600 mb-4">
              Choose a student and curriculum year from the dropdowns above to see their annual progress: Builtin catalog topics, Custom catalog topics, topics in the
              student&apos;s plan, and topics completed.
            </p>
            <p className="text-sm text-gray-500">Add students in Profiles if you do not have any yet.</p>
          </div>
        )}

        {viewState === VIEW.loading && (
          <div className="flex justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue" />
              <span className="text-gray-600">Loading…</span>
            </div>
          </div>
        )}

        {viewState === VIEW.ready && reportData && (
          <div className="space-y-8">
            {reportData.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <PieChart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No data for this year</h3>
                <p className="text-gray-600">
                  There are no topics in this student’s curriculum for the selected year. Add topics in Plan first.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {reportData.map((subject) => {
                  const subjectName = subject.e291_SubjectName ?? 'Subject';
                  const lib = Number(subject.e291_LessonsInLibrary ?? 0);
                  const custom = Number(subject.e291_CustomTopicsInCurriculum ?? 0);
                  const myCurr = Number(subject.e291_LessonsInStudentCurriculum ?? 0);
                  const done = Number(subject.e291_LessonsCompleted ?? 0);

                  const chartData = [
                    {
                      name: subjectName,
                      [CHART_KEYS.builtIn]: lib,
                      [CHART_KEYS.custom]: custom,
                      [CHART_KEYS.inPlan]: myCurr,
                      [CHART_KEYS.completed]: done,
                    },
                  ];
                  const rowTotal = lib + custom + myCurr + done;
                  const xAxisMax = Math.max(rowTotal, 1);
                  const stackValues = {
                    [CHART_KEYS.builtIn]: lib,
                    [CHART_KEYS.custom]: custom,
                    [CHART_KEYS.inPlan]: myCurr,
                    [CHART_KEYS.completed]: done,
                  } as const;

                  return (
                    <div
                      key={subjectName}
                      className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden"
                    >
                      <div className="p-4 space-y-4">
                        <div className="space-y-2 w-full">
                          <span className="block font-semibold text-gray-900 text-lg">{subjectName}</span>
                          <div className="w-full space-y-1.5">
                            <span className="text-xs font-semibold text-gray-700">Topic Counts</span>
                            <span className="text-sm text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-2">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded bg-slate-300 shrink-0" /> {LABEL_BUILTIN}: {lib}
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded bg-violet-300 shrink-0" /> {LABEL_CUSTOM}: {custom}
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded bg-amber-200 shrink-0" /> {inStudentPlanLabel}: {myCurr}
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded bg-confirm-action shrink-0" /> {LABEL_COMPLETED}: {done}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="h-12 w-full min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                              <XAxis type="number" domain={[0, xAxisMax]} hide allowDecimals={false} />
                              <YAxis type="category" dataKey="name" hide />
                              <Bar
                                dataKey={CHART_KEYS.builtIn}
                                stackId="a"
                                fill="#94a3b8"
                                radius={stackSegmentRadius(CHART_KEYS.builtIn, stackValues)}
                              />
                              <Bar
                                dataKey={CHART_KEYS.custom}
                                stackId="a"
                                fill="#c4b5fd"
                                radius={stackSegmentRadius(CHART_KEYS.custom, stackValues)}
                              />
                              <Bar
                                dataKey={CHART_KEYS.inPlan}
                                stackId="a"
                                fill="#fde68a"
                                radius={stackSegmentRadius(CHART_KEYS.inPlan, stackValues)}
                              />
                              <Bar
                                dataKey={CHART_KEYS.completed}
                                stackId="a"
                                fill={LYW_BRAND.confirmAction}
                                radius={stackSegmentRadius(CHART_KEYS.completed, stackValues)}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
  );
}
